// Flight-scan merge logic, shared by both places a flight screenshot can be
// scanned: the itinerary's top-level "Scan" tab (ScanSheet) and the per-item
// "Add a flight" panel on the Flights checklist item (FlightScanPanel).
// Both write into the SAME bookings.flights_details record — that's what
// makes a scanned flight actually show up in the Bookings tab and on the
// itinerary — using a unified `legs[]` list.
//
// Legs are NOT forced into "outbound/return/connecting" buckets — that
// model breaks the moment a trip has more than two flights (SIN→BCN,
// BCN→BRU, BRU→SIN is three legitimate legs, not "outbound + a connecting
// flight"). Instead every leg is just a flight: {flightNo, date, depTime,
// depAirport/origin, arrTime, arrAirport/dest, airline}, and legs are
// always displayed sorted chronologically, labeled by their actual route
// (e.g. "BCN → BRU"). That's what makes any number of flights — a one-way,
// a round trip, a round trip with a layover, or a multi-city itinerary —
// read correctly with no guessing about direction.
//
// The confirmation/passenger name are each leg's OWN fields, not a shared
// trip-level record — same reasoning as hotel stays (see applyHotelScan
// below): a shared field can go stale (still showing after every leg it
// belonged to is gone) and doesn't actually make sense once a multi-city
// trip can have flights under different bookings/references. FlightScanPanel
// still offers one "ref/passenger" input that's reused for convenience when
// adding several legs under the same booking in one sitting, but what's
// SAVED is a copy stamped onto each leg, not one shared object field.
//
// The scan-screenshot/scan-text API returns a `legs: [{flightNo, date,
// depTime, depAirport, arrTime, arrAirport}]` array — every flight segment
// visible, in chronological order — which maps directly onto this shape.
//
// Legs never replace each other on rescan — every scan/manual entry
// appends; use removeFlightLeg to delete one or updateFlightLeg to correct
// one in place.

let legSeq = 0;
const makeLegId = () => `leg_${Date.now()}_${(legSeq++).toString(36)}`;

// Merges one scan-screenshot/scan-text/manual result into an existing
// bookings.flights_details record (or {} for the first scan).
// raw.legs: [{flightNo, date, depTime, depAirport, arrTime, arrAirport}]
// raw.bookingRef/passengerName are copied onto every leg created by this call.
export function applyFlightScan(existing, raw) {
  const legs = existing?.legs ? [...existing.legs] : [];
  const segments = (Array.isArray(raw.legs) ? raw.legs : []).filter(s => s && (s.flightNo || s.date || s.depTime));
  segments.forEach(seg => {
    legs.push({
      id: makeLegId(),
      airline: raw.airline || '',
      flightNo: seg.flightNo || '',
      date: seg.date || '',
      depTime: seg.depTime || '',
      arrTime: seg.arrTime || '',
      origin: seg.depAirport || '',
      dest: seg.arrAirport || '',
      confirmation: raw.bookingRef || '',
      passengerName: raw.passengerName || '',
      screenshot: raw.screenshotUrl || '',
    });
  });

  return { legs };
}

export function removeFlightLeg(existing, legId) {
  return { legs: (existing?.legs || []).filter(l => l.id !== legId) };
}

// Patches one leg in place (flightNo/date/times/airports/airline) without
// touching its id. patch keys not provided are left unchanged.
export function updateFlightLeg(existing, legId, patch) {
  return {
    ...existing,
    legs: (existing?.legs || []).map(l => (l.id === legId ? { ...l, ...patch } : l)),
  };
}

// Numeric timestamp comparison (not string comparison — dates aren't always
// clean zero-padded YYYY-MM-DD, e.g. a manually-typed or oddly-scanned
// date). Legs with no usable date sort to the END, not the front, so one
// missing date doesn't scramble an otherwise-correct order.
const legTimestamp = (leg) => {
  if (!leg.date) return Infinity;
  const t = new Date(`${leg.date}T${leg.depTime || '00:00'}`).getTime();
  return Number.isNaN(t) ? Infinity : t;
};
export const sortLegsChrono = (legs) =>
  (legs || []).slice().sort((a, b) => legTimestamp(a) - legTimestamp(b));

// ── Hotel stays ──────────────────────────────────────────────────────────────
// Same shape as flight legs — bookings.accommodation_details.legs[], one
// entry per stay, always displayed sorted by check-in date. Unlike flights,
// each stay carries its own confirmation/notes (no shared trip-level PNR to
// go stale) — every field lives on the leg itself, so removing a stay can
// never leave an orphaned reference behind.
export function applyHotelScan(existing, raw) {
  const legs = existing?.legs ? [...existing.legs] : [];
  legs.push({
    id: makeLegId(),
    hotelName: raw.hotelName || '',
    checkIn: raw.checkInDate || '',
    checkOut: raw.checkOutDate || '',
    confirmation: raw.bookingRef || '',
    notes: raw.notes || '',
    screenshot: raw.screenshotUrl || '',
  });
  return { legs };
}

export function removeHotelLeg(existing, legId) {
  return { legs: (existing?.legs || []).filter(l => l.id !== legId) };
}

export function updateHotelLeg(existing, legId, patch) {
  return {
    legs: (existing?.legs || []).map(l => (l.id === legId ? { ...l, ...patch } : l)),
  };
}

const hotelTimestamp = (leg) => {
  if (!leg.checkIn) return Infinity;
  const t = new Date(`${leg.checkIn}T12:00:00`).getTime();
  return Number.isNaN(t) ? Infinity : t;
};
export const sortHotelLegsChrono = (legs) =>
  (legs || []).slice().sort((a, b) => hotelTimestamp(a) - hotelTimestamp(b));

// ── Activity/other bookings ─────────────────────────────────────────────────
// Same self-contained-leg pattern as hotel stays — bookings.activities_details
// .legs[], one entry per booked activity (tour, ticket, reservation, etc.),
// shown in the order added (no single natural sort date the way flights/
// hotels have).
export function applyActivityScan(existing, raw) {
  const legs = existing?.legs ? [...existing.legs] : [];
  legs.push({
    id: makeLegId(),
    title: raw.activityName || raw.title || '',
    date: raw.activityDate || raw.date || '',
    time: raw.activityTime || raw.time || '',
    notes: raw.notes || raw.activityDetails || raw.summary || '',
    confirmation: raw.bookingRef || '',
    screenshot: raw.screenshotUrl || '',
  });
  return { legs };
}

export function removeActivityLeg(existing, legId) {
  return { legs: (existing?.legs || []).filter(l => l.id !== legId) };
}

export function updateActivityLeg(existing, legId, patch) {
  return {
    legs: (existing?.legs || []).map(l => (l.id === legId ? { ...l, ...patch } : l)),
  };
}

// ── Travel insurance ─────────────────────────────────────────────────────────
// Same self-contained-leg pattern — bookings.insurance_details.legs[], one
// entry per policy (a multi-traveler or multi-provider trip can have more
// than one), shown in the order added.
export function applyInsuranceScan(existing, raw) {
  const legs = existing?.legs ? [...existing.legs] : [];
  legs.push({
    id: makeLegId(),
    provider: raw.insuranceProvider || raw.provider || '',
    policyNumber: raw.policyNumber || raw.bookingRef || '',
    hotline: raw.hotline || '',
    coverage: raw.coverage || raw.summary || '',
    notes: raw.notes || '',
    screenshot: raw.screenshotUrl || '',
  });
  return { legs };
}

export function removeInsuranceLeg(existing, legId) {
  return { legs: (existing?.legs || []).filter(l => l.id !== legId) };
}

export function updateInsuranceLeg(existing, legId, patch) {
  return {
    legs: (existing?.legs || []).map(l => (l.id === legId ? { ...l, ...patch } : l)),
  };
}

// ── Mobile roaming ───────────────────────────────────────────────────────────
// Same pattern — bookings.roaming_details.legs[], one entry per SIM/plan
// (each traveler might have their own), shown in the order added.
export function applyRoamingScan(existing, raw) {
  const legs = existing?.legs ? [...existing.legs] : [];
  legs.push({
    id: makeLegId(),
    carrier: raw.roamingCarrier || raw.carrier || '',
    plan: raw.roamingPlan || raw.plan || '',
    activation: raw.activationDate || raw.activation || '',
    notes: raw.notes || raw.summary || '',
    screenshot: raw.screenshotUrl || '',
  });
  return { legs };
}

export function removeRoamingLeg(existing, legId) {
  return { legs: (existing?.legs || []).filter(l => l.id !== legId) };
}

export function updateRoamingLeg(existing, legId, patch) {
  return {
    legs: (existing?.legs || []).map(l => (l.id === legId ? { ...l, ...patch } : l)),
  };
}

// ── "Requires booking" ideas (BookableIdeaItem in BookingScreen.js) ────────
// A flat { confirmationNo, notes } record per idea, not a legs[] list — this
// is the shape scanned screenshots need to merge into for the per-idea
// booking rows (e.g. Atomium, a curated Sights idea with requiresBooking).
// Pulls the same fields out of the scan-screenshot/scan-text response that
// applyHotelScan/applyActivityScan pull from, just flattened onto one record
// instead of appended as a leg.
export function mapIdeaScanToFields(result) {
  const m = {};
  if (result?.bookingRef) m.confirmationNo = result.bookingRef;
  const note = result?.activityDetails || result?.hotelDetails || result?.flightDetails || result?.summary;
  if (note) m.notes = note;
  return m;
}

// trip: { dateStart, dateEnd, bookings } — only the fields read here.
// details: the raw scan result plus optional bookingRef/passengerName overrides.
// Returns { dateStart?, dateEnd?, bookings } to spread into the trip update.
export function mergeScannedTripDetails(trip, details) {
  const updates = {};
  const bookings = { ...(trip?.bookings || {}) };

  if (details.type === 'flight' || details.flightDetails || (Array.isArray(details.legs) && details.legs.length > 0)) {
    const existing = bookings['flights_details'] || {};
    const merged = applyFlightScan(existing, details);
    bookings['flights_details'] = merged;

    // Trip start/end = earliest/latest flight date across every leg —
    // works the same whether it's a one-way, a round trip, or a multi-city
    // itinerary with several flights.
    const dates = merged.legs.filter(l => l.date).map(l => l.date).sort();
    if (dates.length && !trip?.dateStart) updates.dateStart = dates[0];
    if (dates.length > 1 && !trip?.dateEnd) updates.dateEnd = dates[dates.length - 1];
  }

  if (details.checkInDate && !trip?.dateStart) updates.dateStart = details.checkInDate;
  if (details.checkOutDate && !trip?.dateEnd) updates.dateEnd = details.checkOutDate;

  if (details.type === 'hotel' || details.hotelDetails) {
    bookings['accommodation_details'] = applyHotelScan(bookings['accommodation_details'] || {}, details);
  }

  updates.bookings = bookings;
  return updates;
}
